import { isFirebaseConfigured } from "@/auth/firebase";
import {
  callCommitRaidHomeStructureDamage,
  callSyncHomeStructureDefense,
  callUseHomeStructurePotion,
} from "@/cloud/game-callables";
import type { HomeEconomy } from "@/home/economy";
import { applyStructurePotionHeal } from "@/home/home-structure-defense-logic";
import {
  loadHomeStructureDefenseLocal,
  saveHomeStructureDefenseLocal,
} from "@/home/home-structure-idb";
import { buildHomeStructureCombatSnapshot } from "@/home/home-structure-snapshot";
import {
  bindHomeStructureStatusModal,
  renderHomeStructureStatusModalHtml,
} from "@/home/home-structure-status-modal";
import type { HomeStructureCombatSnapshot, HomeStructureDefenseState } from "@/home/home-structure-types";
import { homeStructureHpPotions } from "@/home/home-structure-economy";
import { getGameSettings } from "@/game/model/game-settings";
import type { HomeRoomData } from "@/home/storage";
import type { PhaserBootOptions } from "@/game/main";
import { HomeEvents, type HomeStructureAssaultEndedPayload } from "@/home/home-events";

export type HomeStructureMountContext = {
  userId: string;
  getEconomy: () => HomeEconomy;
  setEconomy: (economy: HomeEconomy) => void;
  getHomeData: () => HomeRoomData;
  persistEconomy: () => void;
  isEditMode: () => boolean;
  structureRaid?: {
    defenderUid: string;
    runId: string;
    onAssaultEnded: (detail: { damageThisRun: number; destroyed: boolean }) => void;
  };
};

export type HomeStructureMountHandle = {
  refreshDefense: () => Promise<void>;
  getBootExtras: () => Pick<
    PhaserBootOptions,
    "homeStructureCombat" | "homeStructureOwnerUid"
  >;
  syncStructureChrome: () => void;
  teardown: () => void;
};

export function mountHomeStructureFeatures(
  root: HTMLElement,
  ctx: HomeStructureMountContext,
): HomeStructureMountHandle {
  let defenseState = loadHomeStructureDefenseLocal(ctx.userId);
  let snapshot = buildHomeStructureCombatSnapshot(
    ctx.getEconomy(),
    ctx.getHomeData(),
    defenseState,
  );
  let autoTickTimer: ReturnType<typeof setInterval> | null = null;

  const existing = root.querySelector("#home-structure-status-modal");
  if (!existing) {
    root.insertAdjacentHTML(
      "beforeend",
      renderHomeStructureStatusModalHtml(snapshot, ctx.getEconomy()),
    );
  }

  const statusBtnId = "home-structure-status-open";
  if (!root.querySelector(`#${statusBtnId}`)) {
    const rail = root.querySelector(".home-rail-primary");
    if (rail) {
      rail.insertAdjacentHTML(
        "beforeend",
        `<button type="button" id="${statusBtnId}" class="home-rail-btn home-rail-btn--tile home-structure-rail-btn" aria-label="สเตตัสบ้าน">
          <span class="home-rail-label">บ้าน</span>
        </button>`,
      );
    }
  }

  const persistDefenseLocal = (state: HomeStructureDefenseState) => {
    defenseState = state;
    saveHomeStructureDefenseLocal(ctx.userId, state);
  };

  const rebuildSnapshot = () => {
    snapshot = buildHomeStructureCombatSnapshot(
      ctx.getEconomy(),
      ctx.getHomeData(),
      defenseState,
    );
    return snapshot;
  };

  const pullCloudDefense = async (defenderUid: string) => {
    if (!isFirebaseConfigured()) return;
    try {
      const res = await callSyncHomeStructureDefense({ defenderUid });
      persistDefenseLocal({
        damageTaken: res.damageTaken,
        lastDamageAt: res.lastDamageAt,
        brokenUntil: res.brokenUntil,
      });
      if (res.potionsLeft != null && defenderUid === ctx.userId) {
        const economy = ctx.getEconomy();
        const wallet = { ...economy.wallet, homeStructureHpPotions: res.potionsLeft };
        if (wallet.homeStructureHpPotions !== economy.wallet.homeStructureHpPotions) {
          ctx.setEconomy({ ...economy, wallet });
          ctx.persistEconomy();
        }
      }
    } catch {
      /* offline */
    }
  };

  const modal = bindHomeStructureStatusModal(root, {
    getEconomy: ctx.getEconomy,
    setEconomy: ctx.setEconomy,
    getSnapshot: () => snapshot,
    refreshSnapshot: rebuildSnapshot,
    onEconomyChanged: () => {
      ctx.persistEconomy();
      rebuildSnapshot();
    },
    onUsePotion: async () => {
      const economy = ctx.getEconomy();
      if (homeStructureHpPotions(economy) <= 0) return "ไม่มียา HP บ้าน";
      if (isFirebaseConfigured()) {
        try {
          const res = await callUseHomeStructurePotion({});
          persistDefenseLocal({
            damageTaken: res.damageTaken,
            lastDamageAt: res.lastDamageAt,
            brokenUntil: res.brokenUntil,
          });
          ctx.setEconomy({
            ...economy,
            wallet: {
              ...economy.wallet,
              homeStructureHpPotions: res.potionsLeft,
            },
          });
          ctx.persistEconomy();
          rebuildSnapshot();
          return null;
        } catch (err) {
          return err instanceof Error ? err.message : "ใช้ยาไม่สำเร็จ";
        }
      }
      const heal = getGameSettings().homeStructurePotionHeal;
      persistDefenseLocal(applyStructurePotionHeal(defenseState, heal));
      ctx.setEconomy({
        ...economy,
        wallet: {
          ...economy.wallet,
          homeStructureHpPotions: homeStructureHpPotions(economy) - 1,
        },
      });
      ctx.persistEconomy();
      rebuildSnapshot();
      return null;
    },
  });

  const syncStructureChrome = () => {
    const btn = root.querySelector<HTMLElement>(`#${statusBtnId}`);
    if (btn) btn.hidden = ctx.isEditMode();
    rebuildSnapshot();
    modal.refresh();
  };

  root.querySelector(`#${statusBtnId}`)?.addEventListener("click", () => {
    if (ctx.isEditMode()) return;
    void pullCloudDefense(ctx.userId).then(() => {
      rebuildSnapshot();
      modal.open();
    });
  });

  const onStructureAssaultEnded = (event: Event) => {
    const detail = (event as CustomEvent<HomeStructureAssaultEndedPayload>).detail;
    const damageThisRun = Math.max(0, Math.floor(detail.damageThisRun ?? 0));
    if (ctx.structureRaid) {
      const defenderUid = ctx.structureRaid.defenderUid;
      void (async () => {
        if (isFirebaseConfigured()) {
          try {
            const res = await callCommitRaidHomeStructureDamage({
              runId: ctx.structureRaid!.runId,
              defenderUid,
              damageThisRun,
            });
            persistDefenseLocal({
              damageTaken: res.damageTaken,
              lastDamageAt: res.lastDamageAt,
              brokenUntil: res.brokenUntil,
            });
            rebuildSnapshot();
            ctx.structureRaid!.onAssaultEnded({
              damageThisRun: res.damageApplied ?? damageThisRun,
              destroyed: res.destroyed === true,
            });
            return;
          } catch {
            /* fall through local */
          }
        }
        const maxHp = snapshot.maxHp;
        const next = {
          ...defenseState,
          damageTaken: Math.min(maxHp, defenseState.damageTaken + damageThisRun),
          lastDamageAt: Date.now(),
        };
        if (next.damageTaken >= maxHp) {
          const shieldMs = getGameSettings().homeStructureShieldHours * 3_600_000;
          next.brokenUntil = Math.max(next.brokenUntil, Date.now() + shieldMs);
        }
        persistDefenseLocal(next);
        const destroyed = detail.destroyed || next.damageTaken >= maxHp;
        ctx.structureRaid!.onAssaultEnded({ damageThisRun, destroyed });
        rebuildSnapshot();
      })();
    }
  };

  window.addEventListener(HomeEvents.StructureAssaultEnded, onStructureAssaultEnded);

  autoTickTimer = setInterval(() => {
    if (ctx.isEditMode()) return;
    void pullCloudDefense(ctx.userId).then(() => rebuildSnapshot());
  }, 30_000);

  void pullCloudDefense(ctx.userId).then(() => rebuildSnapshot());

  return {
    refreshDefense: async () => {
      await pullCloudDefense(ctx.userId);
      rebuildSnapshot();
    },
    getBootExtras: () => {
      const raid = ctx.structureRaid;
      const ownerUid = ctx.userId;
      if (raid) {
        const combat: HomeStructureCombatSnapshot & {
          sessionBaseDamage: number;
          raidMode: true;
        } = {
          ...snapshot,
          sessionBaseDamage: defenseState.damageTaken,
          raidMode: true,
        };
        return {
          homeStructureOwnerUid: ownerUid,
          homeStructureCombat: {
            ...combat,
            defenderUid: raid.defenderUid,
            allowAttack: raid.defenderUid !== ownerUid,
          },
        };
      }
      return {
        homeStructureOwnerUid: ownerUid,
        homeStructureCombat: {
          ...snapshot,
          raidMode: false,
          allowAttack: false,
        },
      };
    },
    syncStructureChrome,
    teardown: () => {
      if (autoTickTimer) clearInterval(autoTickTimer);
      window.removeEventListener(HomeEvents.StructureAssaultEnded, onStructureAssaultEnded);
    },
  };
}
